/**
 * SEBIT VCloud SSO helpers — reading the hand-off SEBIT puts in the landing URL.
 *
 * SEBIT is the identity provider here: the learner clicks the CATCHUPPER tile in their digital campus and
 * arrives at `/login/with-sebit?token=…&clientid=…`. We forward that pair to our backend, which validates the
 * token against SEBIT's own service and hands back where to go next.
 *
 * Nothing about SEBIT is configured in the frontend — unlike `K12LoginUtilization`, which has to build an
 * authorize URL and therefore needs `REACT_APP_K12_*` to be present. There is consequently no
 * "is it configured" check to make here: whether this environment can talk to SEBIT is decided by the
 * backend's `SEBIT_CLIENT_ID`, and it answers with a specific error when it cannot.
 *
 * Pure functions with no React or request dependency, so they are unit-testable — see
 * `SebitLoginUtilization.test.ts`.
 */

/** The pair SEBIT puts in the landing URL's query string. */
export type SebitLandingParams = {
  token: string | null;
  clientId: string | null;
};

/**
 * Reads SEBIT's `token` and `clientid` out of a query string.
 *
 * `clientid` is all lower-case on the wire — that is the spelling in SEBIT's specification, and it is read
 * exactly as sent rather than normalised, because a case-insensitive lookup would quietly accept a URL shape
 * their platform does not actually produce and mask a genuine change on their side.
 *
 * Blank values are treated as absent. A `?token=&clientid=` URL is a broken hand-off, not a hand-off carrying
 * two empty credentials, and letting empty strings through would have the backend compare a blank `clientid`
 * against its configuration.
 */
export const readSebitLandingParams = (
  search?: string | null
): SebitLandingParams => {
  const params = new URLSearchParams((search ?? "").replace(/^\?/, ""));
  const token = params.get("token");
  const clientId = params.get("clientid");
  return {
    token: token && token.trim().length > 0 ? token : null,
    clientId: clientId && clientId.trim().length > 0 ? clientId : null,
  };
};

/**
 * Whether this URL is SEBIT starting a login (as opposed to our own backend handing tokens back).
 *
 * The same route serves both legs — SEBIT sends the user in with a query string, and the hand-off returns
 * them with a fragment — so the screen has to tell the two apart. Deciding on "is there a token in the
 * query" rather than "is the fragment empty" keeps the two conditions from both being true at once on a
 * re-entry.
 */
export const isSebitEntry = (search?: string | null): boolean =>
  readSebitLandingParams(search).token !== null;
